import {
  Component,
  OnInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Community } from '@models/communities/community.model';
import { SignUpStep0Component } from '@modules/sign-up/components/sign-up-step0/sign-up-step0.component';
import { SignUpStep1Component } from '@modules/sign-up/components/sign-up-step1/sign-up-step1.component';
import { AuthService } from '@services/auth/auth.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AppRoutes } from 'app/routes';
import { EnterPhoneRes } from '@models/sign-up/enter-phone-res.model';
import { VerifyEmailStepComponent } from '@modules/sign-up/components/verify-email-step/verify-email-step.component';
import { VerifyPhoneStepComponent } from '@modules/sign-up/components/verify-phone-step/verify-phone-step.component';
import { RegisterSuccess } from '@models/sign-up/register-success.model';
import { BackService } from '@services/shared/back.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LoginRequest } from '@models/users/dto/login.request';
import { UserService } from '@services/auth/user.service';
import { NotificationService } from '@services/shared/notification.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SignUpReferralStepComponent } from '@modules/sign-up/components/sign-up-referral-step/sign-up-referral-step.component';
import { GlobalSettingsStore } from '@services/stores/global_settings.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalSettings } from '@models/users/global_settings.model';
import { environment as env } from '@environments/environment';
import { UtmService } from '@services/shared/utm.service';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    SignUpStep0Component,
    SignUpStep1Component,
    SignUpReferralStepComponent,
    VerifyEmailStepComponent,
    VerifyPhoneStepComponent,
  ],
  providers: [UserService, NotificationService],
  templateUrl: './sign-up.page.html',
  styleUrl: './sign-up.page.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SignUpPage implements OnInit, OnDestroy {
  step: number = 0;
  email?: string;
  password?: string;
  phone?: string;
  userId?: number;
  selectedOption: string = 'phone';
  isWrongCode: boolean = false;

  hasNoAccount = false;

  formValue?: any;
  referralCode?: string;

  otpCode?: string;

  signUpIsInProgress = false;

  globalSettings?: GlobalSettings;

  init = false;

  platform = 'partypage';
  @Input() isCommunity: boolean = false;
  @Input() community?: Community;
  @Output() login = new EventEmitter();
  @Output() accountCreated = new EventEmitter();

  /** Subject that emits when the component has been destroyed. */
  private _onDestroy = new Subject<void>();

  plan?: any;
  pricingId?: string | null;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private backService: BackService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private globalSettingsStore: GlobalSettingsStore,
    private utmService: UtmService,
  ) {
    this.platform = env.platformShort;

    if (this.platform !== 'partypage') {
      this.selectedOption = 'email';
    }

    this.globalSettingsStore.globalSettings
      .pipe(takeUntilDestroyed())
      .subscribe((globalSettings) => {
        this.globalSettings = globalSettings;

        if (this.globalSettings && !this.init) {
          this.init = true;
          const snapshot = this.activatedRoute.snapshot;

          this.utmService.captureFromQueryParams(snapshot.queryParamMap);

          if (snapshot.queryParamMap.has('plan')) {
            this.plan = snapshot.queryParamMap.get('plan');
          }

          if (snapshot.queryParamMap.has('pricingId')) {
            this.pricingId = snapshot.queryParamMap.get('pricingId');
          }

          // when no account for email/phone on login flow
          if (
            snapshot.queryParamMap.has('email') &&
            !this.globalSettings?.registerWithoutEmail
          ) {
            const email = snapshot.queryParamMap.get('email');
            if (email && email !== '') {
              this.email = email;
              this.hasNoAccount = true;
              this.selectedOption = 'email';
              this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { email: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
              });
            }
          }
          if (snapshot.queryParamMap.has('phone')) {
            const phone = snapshot.queryParamMap.get('phone');
            if (phone && phone !== '') {
              this.phone = phone;
              this.hasNoAccount = true;
              this.selectedOption = 'phone';
              this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { phone: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
              });
            }
          }

          if (snapshot.queryParamMap.has('ref')) {
            const referralCode = snapshot.queryParamMap.get('ref');
            if (referralCode && referralCode !== '') {
              this.referralCode = referralCode;
            }
          }
        }
      });
  }

  ngOnInit(): void {
    this.backService.backButtonClick$
      .pipe(takeUntil(this._onDestroy))
      .subscribe((clicked) => {
        if (clicked) {
          if (this.step === 1 || this.step === 4) {
            this.step--;
            this.backService.showBackButton(false);
          } else if (this.step === 2) {
            this.step--;
          }
        }
      });
  }

  selectOption(option: string): void {
    this.selectedOption = option;
    if (this.hasNoAccount) {
      this.hasNoAccount = false;
    }
    this.phone = undefined;
    this.email = undefined;
  }

  onPhoneEntered(phone: string) {
    this.phone = phone;
    if (this.isCommunity) {
      this.accountCreated.emit({
        phone: phone,
      });
    } else {
      this.step++;
      this.backService.showBackButton(true);
    }
  }

  onEmailEntered(email: string) {
    this.email = email;
    if (this.isCommunity) {
      this.accountCreated.emit({
        email: email,
      });
    } else {
      this.step++;
      this.backService.showBackButton(true);
    }
  }

  onLogin() {
    this.login.emit();
  }
  onRegisterSuccess(res: RegisterSuccess) {
    this.userId = res.userId;
    this.password = res.password;

    if (!this.isCommunity) {
      this.step++;
    } else {
      if ((!this.referralCode || this.referralCode === '') && this.step === 1) {
        this.step = 3;
      } else {
        this.step++;
      }
    }
    this.backService.showBackButton(false);
  }

  signUpFromForm(value: any): void {
    this.formValue = value;
    this.signUp();

    // this.step++;
  }

  signUpWReferral(referralCode: string) {
    this.referralCode = referralCode;
    if (!this.isCommunity) {
      this.loginAndRedirectToWelcome(referralCode);
    }
  }

  signUp() {
    if (!this.formValue || this.signUpIsInProgress) {
      return;
    }

    this.signUpIsInProgress = true;

    if (this.selectedOption === 'email') {
      this.userService
        .register(
          this.email,
          this.formValue.password,
          this.formValue.name,
          this.translateService.currentLang,
          this.utmService.getUtmParams(),
          // this.referralCode,
        )
        .pipe(finalize(() => (this.signUpIsInProgress = false)))
        .subscribe({
          next: (res) => {
            this.onRegisterSuccess({
              userId: res.id,
              password: this.formValue.password,
            });

            this.utmService.clear();
          },
          error: (err) => {
            this.handleSignUpError(err);
          },
        });
    } else if (this.selectedOption === 'phone') {
      this.userService
        .registerWithPhone(
          this.phone,
          this.formValue.name,
          this.translateService.currentLang,
          this.utmService.getUtmParams(),
          // this.referralCode
        )
        .pipe(finalize(() => (this.signUpIsInProgress = false)))
        .subscribe({
          next: (res) => {
            this.onRegisterSuccess({
              userId: res.id,
            });

            this.utmService.clear();
          },
          error: (err) => {
            this.handleSignUpError(err);
          },
        });
    }
  }

  handleSignUpError(err: any): void {
    if (err && err.error?.startsWith('bad request - invalid referral code')) {
      this.notificationService.error(
        this.translateService.instant('APP.AUTH.ERRORS.INVALID_REFERRAL_CODE'),
        this.translateService.instant(
          'APP.AUTH.ERRORS.COULD_NOT_SIGN_UP_TITLE',
        ),
      );

      this.isWrongCode = true;

      if (this.step === 1) {
        this.step = 2;
      }
    } else {
      this.notificationService.error(
        this.translateService.instant('APP.AUTH.ERRORS.COULD_NOT_SIGN_UP'),
      );
    }
  }

  changePhone() {
    this.selectedOption = 'phone';
    this.step = 0;
  }
  onVerifyEmailSuccess() {
    // this.loginAndRedirectToWelcome();
    this.step++;
  }

  onEnterPhoneRes(res: EnterPhoneRes) {
    if (res.skipped) {
      this.loginAndRedirectToWelcome();
    } else {
      this.phone = res.phone;
      this.step++;
    }
  }

  onVerifyPhoneSuccess(code: string) {
    // this.loginAndRedirectToWelcome(code);
    this.otpCode = code;
    this.step++;
  }

  private loginAndRedirectToWelcome(referralCode?: string) {
    if (this.email && this.password && this.password !== '') {
      const loginReq: LoginRequest = {
        email: this.email,
        password: this.password,
        referralCode: referralCode,
      };

      this.authService.login(
        loginReq,
        false,
        () => {
          if (this.community) {
            this.accountCreated.emit();
          } else {
            if (this.pricingId) {
              this.pricingPlanPreselected();
            } else {
              this.router.navigate(['/', AppRoutes.Customer.welcome]);
            }
          }
        },
        () => {},
      );
    } else if (this.phone) {
      const loginReq: LoginRequest = {
        phone: this.phone,
        otp: this.otpCode,
        referralCode: referralCode,
      };

      this.authService.loginWithOtp(
        loginReq,
        false,
        () => {
          if (this.pricingId) {
            this.pricingPlanPreselected();
          } else {
            this.router.navigate(['/', AppRoutes.Customer.welcome]);
          }
        },
        () => {
          this.isWrongCode = true;
        },
      );
    } else {
      this.router.navigate(['/', AppRoutes.Customer.welcome]);
    }
  }

  pricingPlanPreselected() {
    this.router.navigate(['/', AppRoutes.Customer.Payments.stripeCheckout], {
      queryParams: {
        priceId: this.pricingId,
        redirectUrl: encodeURIComponent(
          `${env.website}/${AppRoutes.Customer.welcome}`,
        ),
        backUrl: encodeURIComponent(`/${AppRoutes.Customer.welcome}`),
      },
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
